import { NotificationService } from '../../shared/notification/notification.service';
import { ProfileService } from '../../profiles/shared/profile.service';
import { Contact } from '../../contacts/shared/contact.model';
import { ProfileContactEditComponent } from '../../profiles/profile-contacts/profile-contact-edit/profile-contact-edit.component';
import { TranslationService } from '../../shared/translation/translation.service';
import { WlUpperCaseFirstLetterPipe } from '../../shared/pipes/ucfirst.pipe';
import { MenuItem, ConfirmationService, SelectItem } from 'primeng/api';
import { SalesOpportunityDto } from '../../sales-opportunities/shared/dto/sales-opportunity.dto';
import { PagedResultGeneric } from '../../shared/model/paged-result.model';
import { UserRead } from '../../users/shared/user-read.model';
import { UserService } from '../../users/shared/user.service';
import { SalesOpportunitySearchDto } from '../../sales-opportunities/shared/dto/sales-opportunity-search.dto';
import { DatagridService } from '../../shared/widgets/datagrid/datagrid.service';
import { SalesOpportunityService } from '../../sales-opportunities/shared/sales-opportunity.service';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { SalesOpportunityEditDialogComponent } from 'src/app/sales-opportunities/sales-opportunity-edit-dialog/sales-opportunity-edit-dialog.component';
import { Table } from 'primeng/table';

@Component({
  selector: 'wl-dashboard-neo-sales-opportunities',
  templateUrl: './dashboard-neo-sales-opportunities.component.html',
  styleUrls: ['./dashboard-neo-sales-opportunities.component.less'],
    standalone: false
})
export class DashboardNeoSalesOpportunitiesComponent implements OnInit {
  public search: SalesOpportunitySearchDto;
  public searchResult: PagedResultGeneric<SalesOpportunityDto>;
  public loading: boolean;
  public rowMenuItems: Map<number, MenuItem[]>;
  public acceptText: string;
  public rejectText: string;
  public pageSizeOptions: Array<SelectItem>;
  public selectedPageSize: number;

  @ViewChild(Table) myOppsGrid: Table;
  @ViewChild(SalesOpportunityEditDialogComponent) salesOppEditDialog: SalesOpportunityEditDialogComponent;
  @ViewChild(ProfileContactEditComponent) contactEdit: ProfileContactEditComponent;

  constructor(
    private _datagridService: DatagridService,
    private _userService: UserService,
    private _ucFirstPipe: WlUpperCaseFirstLetterPipe,
    private _translationService: TranslationService,
    private _profileService: ProfileService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService,
    private _salesOppService: SalesOpportunityService,
    private cdr: ChangeDetectorRef
  ) {
    this.rowMenuItems = new Map();
  }

  ngOnInit(): void {
    Promise.all([
      this._translationService.getTitlecase('dashboard.sales-opportunities.page-size.show-10'),
      this._translationService.getTitlecase('dashboard.sales-opportunities.page-size.show-all'),
    ]).then((data: string[]) => {
      this.pageSizeOptions = [
        { label: data[0], value: 10 },
        { label: data[1], value: 1001 },
      ];
    });

    this._userService.getCurrentUser().then((data: UserRead) => {
      this.search = {
        IncludeRevenueItem: true,
        IncludeProfile: true,
        IncludeWorkingWithProfile: true,
        Lost: false,
        Closed: false,
        PageSize: 10,
        PageNumber: 1,
        AssignedTo: data.UserNum,
        IncludeLastContactedDate: true,
      } as SalesOpportunitySearchDto;

      this.loadSalesOpportunities();
    });
  }

  public lazyLoadSalesOpportunities($event) {
    if (this.search) {
      this._datagridService.mapDatagridPagingToPagedSearch($event, this.search);

      this.loadSalesOpportunities();
    }
  }

  actionItemsForSalesOpp(opp: SalesOpportunityDto): MenuItem[] {
    if (!this.rowMenuItems.has(opp.SalesID)) {
      this.rowMenuItems.set(opp.SalesID, [
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('dashboard.sales-opportunities.actions.edit')),
          icon: 'pi pi-print',
          command: () => this.launchEditDialog(opp.SalesID),
        },
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('dashboard.sales-opportunities.actions.add-contact')),
          icon: 'pi pi-envelope',
          command: () => this.launchAddContact(opp.SalesID),
        },
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('dashboard.sales-opportunities.actions.delete')),
          icon: 'pi pi-envelope',
          command: () => this.deleteOpp(opp.SalesID),
        },
      ]);
    }
    return this.rowMenuItems.get(opp.SalesID);
  }

  public launchEditDialog(oppId: number) {
    this.salesOppEditDialog.open(oppId);
  }

  public launchAddContact(oppId: number) {
    const profileId = this.searchResult?.Result?.find((x) => x.SalesID === oppId)?.ProfileID ?? 0;
    this.contactEdit.profileContact = { ProfileId: profileId, ContactDate: new Date(), SalesID: oppId } as any;
    this.contactEdit.open();
  }

  contactSubmitClicked(contact: Contact) {
    let promise = Promise.resolve();
    if (contact.ContactId) {
      promise = this._profileService.UpdateProfileContact(contact);
    } else {
      promise = this._profileService.CreateProfileContact(contact);
    }
    promise.then(
      () => {
        this.contactEdit.close();
        this.loadSalesOpportunities(true);
      },
      (err) => {
        this._notificationService.error(err);
      }
    );
  }

  public deleteOpp(id: number) {
    this.rejectText = this._translationService.getInstant('profiles.view.sales-opps.cancel').toUpperCase();
    this.acceptText = this._translationService.getInstant('profiles.view.sales-opps.delete-opp-button').toUpperCase();

    this._confirmationService.confirm({
      key: 'deleteSalesOppCD',
      header: this._translationService.getInstantTitlecase('profiles.view.sales-opps.delete-this-opp'),
      message: this._translationService.getInstantCapitalizeFirstCharacter('profiles.view.sales-opps.delete-opp-body'),
      accept: () => {
        this._salesOppService.delete(id).then(() => {
          this.loadSalesOpportunities(true);
        });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  public handlePageSizeChange($event) {
    if ($event) {
      this.search.PageSize = $event;
      this.myOppsGrid.rows = $event;
      this.lazyLoadSalesOpportunities({ first: 0, rows: $event });
    }
  }

  private loadSalesOpportunities(forceRefresh?: boolean) {
    this.loading = true;
    this._salesOppService
      .search(this.search, forceRefresh)
      .then((data: PagedResultGeneric<SalesOpportunityDto>) => {
        this.searchResult = data;
        this.cdr.detectChanges();
      })
      .finally(() => {
        this.loading = false;
      });
  }
}
